import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  AddIcon,
  Alert,
  Button,
  Card,
  Col,
  DeleteIcon,
  Dialog,
  Fieldset,
  FormContainer,
  FormItem,
  Input,
  Modal,
  NativeSelect,
  RemoveRedEyeOutlinedIcon,
  Row,
  Select,
} from 'elmo-elements';
import { yupResolver } from '@hookform/resolvers/yup';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';

import { ElearningParamType } from '@/pages/Module/routes';
import { useGetElearningQuery } from '@/modules/services';
import { ResourceMetadataType, ResourceType } from '@/modules/types/resource';
import {
  useDeleteResourceMutation,
  usePostResourceMutation,
} from '@/modules/services/resources';

import ContainerForm from '@/components/ContainerForm';

import './ModuleResources.scss';
import CKFileUpload from '@/components/CKFileUpload/CKFileUpload';
import {
  AssetEntityEnum,
  AssetFileTypeEnum,
  CkFinderResourceTypeEnum,
} from '@/modules/types/ckFinder';
import { getAssetLocation, getCkFileType, getCkFinderConfig } from '@/lib/util';
import { useUpdateElearningMutation } from '@/modules/services/myElearnings';
import { useDispatch } from 'react-redux';
import { SelectPropsOption } from 'elmo-elements/dist/Select';

interface ShowResourceState extends SelectPropsOption {
  error?: string;
}

const resourceOptions = [
  {
    value: '1',
    label: 'Yes',
  },
  {
    value: '0',
    label: 'No',
  },
];

const ModuleResources = (): React.ReactElement => {
  const handleError = useErrorHandler();
  const dispatch = useDispatch();
  const { elearningId } = useParams<ElearningParamType>();

  const elearning = useGetElearningQuery(elearningId);
  const [showResource, setShowResource] = useState<ShowResourceState>({
    ...resourceOptions[0],
  });

  const [postResource, postResourceStatus] = usePostResourceMutation();
  const [deleteResource] = useDeleteResourceMutation();

  interface CreateResourceForm {
    resourceName: string;
    resourceType: string;
    resourceURL: string;
  }

  // Using a map because the keys are used in the DB
  const resourceTypes = {
    PDF: ResourceMetadataType.PDF,
    WordDoc: ResourceMetadataType.WORD,
    ExcelDoc: ResourceMetadataType.EXCEL,
    PowerpointDoc: ResourceMetadataType.POWERPOINT,
    Link: ResourceMetadataType.LINK,
  };

  const schema = yup.object().shape({
    resourceName: yup
      .string()
      .max(255)
      .transform((curr, orig) => (orig.trim() === '' ? null : curr))
      .required('Resource Name is required'),
    resourceType: yup.number().required('Resource Type is required'),
    resourceURL: yup
      .string()
      .max(1000)
      .transform((curr, orig) => (orig.trim() === '' ? null : curr))
      .required('Resource URL is required'),
  });

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<CreateResourceForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      resourceName: '',
      resourceType: undefined,
      resourceURL: '',
    },
  });

  const handleValueTargetChange = (e: any, name: string) => {
    setValue(name as keyof CreateResourceForm, e.target.value);
  };

  const handleValueChange = (e: any, name: string) => {
    setValue(name as keyof CreateResourceForm, e);
  };

  const handleImgUpload = (currentFileName: string) => {
    setValue('resourceURL', currentFileName);
  };

  const getResourcePath = (filename: string) =>
    getAssetLocation({
      tenantId: elearning.data ? elearning.data.tenantId : '',
      entityId: elearning.data ? elearning.data?.id : '',
      entity: AssetEntityEnum.elearning,
      fileType: AssetFileTypeEnum.RESOURCES,
      fileName: filename,
    });

  const [modalOpen, setModalOpen] = useState(false);
  const [updateElearning, updateElearningStatus] = useUpdateElearningMutation();

  useEffect(() => {
    if (!elearning.data) return;

    setShowResource(
      elearning.data.playerMetadata.showResource
        ? resourceOptions[0]
        : resourceOptions[1],
    );
  }, [elearning]);

  if (elearning.isError) {
    handleError(elearning.error);
  }

  const resources = elearning.data?.resources;

  async function onSubmit(formData: CreateResourceForm) {
    await postResource({
      elearningId,
      payload: {
        name: formData.resourceName,
        elearningResourceMetadata: {
          position: resources?.length ?? 0,
          type: Number(formData.resourceType),
          url: formData.resourceURL,
        },
      },
    });
    setModalOpen(false);
    reset();
  }

  async function handleDeleteResource(resourceId: string) {
    await deleteResource(resourceId);
  }

  return (
    <div id="ModuleResourcesList">
      <ContainerForm maxColumns={1}>
        <ContainerForm maxColumns={1}>
          <FormItem
            label="Show Resource"
            message={showResource.error}
            status={showResource.error ? 'error' : 'default'}
            size="sm"
          >
            <Select
              value={{
                value: `${showResource.value}`,
                label: showResource.label,
              }}
              closeMenuOnSelect={true}
              onChange={(e: any) => {
                setShowResource({
                  value: `${e.value}`,
                  label: e?.label,
                  error: '',
                });
              }}
              options={resourceOptions}
              ariaLabel="show resource"
            />
          </FormItem>
        </ContainerForm>
        {elearning.isLoading || !elearning.data ? (
          <div aria-label="Module Resources" />
        ) : resources === undefined || resources.length === 0 ? (
          <Card>No resources have been added yet.</Card>
        ) : (
          <>
            {resources.map((d: ResourceType) => (
              <Card key={`ResourceItem-${d.id}`}>
                <Row justifyContent="between" wrap="nowrap" alignItems="center">
                  <Col>{d.name}</Col>
                  <Col isAuto={true} align="end" shrink={0}>
                    <Button
                      icon={<RemoveRedEyeOutlinedIcon />}
                      onClick={(_) => {
                        const resourceUrl =
                          d.elearningResourceMetadata.url.includes('/')
                            ? d.elearningResourceMetadata.url
                            : getResourcePath(d.elearningResourceMetadata.url);
                        window.open(resourceUrl, '_blank', 'noopener');
                      }}
                      className="mx-2"
                    />
                    <Button
                      onClick={async (e) => {
                        e.preventDefault();
                        Dialog.confirm({
                          title: 'Are you sure want to remove this resource?',
                          content: `Resource ${d.name} will be removed from this course, but will still be available in your media library`,
                          async onOk() {
                            await handleDeleteResource(d.id);
                          },
                        });
                      }}
                      icon={<DeleteIcon />}
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </>
        )}
        <Row className="mt-4">
          <Col>
            <Button
              onClick={(e) => {
                e.preventDefault();
                setModalOpen(true);
              }}
              icon={<AddIcon />}
              isUppercase={false}
            >
              Add resource
            </Button>
          </Col>
        </Row>
      </ContainerForm>
      <FormItem>
        <Button
          type="primary"
          htmlType="submit"
          ariaLabel="Submit Form"
          className="px-5 mt-5"
          id="show-resource-button"
          isDisabled={updateElearningStatus.isLoading}
          onClick={async (event) => {
            event.preventDefault();
            if (showResource) {
              await updateElearning({
                id: elearning.data?.id,
                playerMetadata: {
                  ...elearning.data?.playerMetadata,
                  showResource: +showResource.value,
                },
              });
              dispatch({
                type: flashMessageActions.addFlashMessage,
                payload: {
                  isActive: true,
                  message: `Resource Saved Successfully`,
                },
              });
              return;
            }

            setShowResource({
              value: '',
              label: '',
              error: 'Please select from the options',
            });
          }}
        >
          Save
        </Button>
      </FormItem>
      <Modal
        id="ModalDialog"
        type="medium"
        isOpened={modalOpen}
        closeModal={() => {
          reset();
          setModalOpen(false);
        }}
        closeLabel="Close"
        title="Add Resource"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <FormContainer>
              {postResourceStatus.error && (
                <Alert
                  message="There was an error submitting the form"
                  type="danger"
                  isCloseable={false}
                />
              )}

              <FormItem
                label="Resource Name*"
                message={errors.resourceName && errors.resourceName?.message}
                status={errors.resourceName ? 'error' : 'default'}
                size="md"
              >
                <Input
                  {...register('resourceName', {})}
                  onChange={(e) => handleValueTargetChange(e, 'resourceName')}
                  value={watch('resourceName')}
                  ariaLabel="Resource Name"
                  id="resource-name"
                />
              </FormItem>
              <FormItem
                label="Resource Type*"
                message={errors.resourceType?.message}
                status={errors.resourceType ? 'error' : 'default'}
                size="md"
              >
                <NativeSelect
                  {...register('resourceType', {})}
                  value={watch('resourceType')}
                  onChange={(e) => handleValueChange(e, 'resourceType')}
                  ariaLabel="Resource Type"
                  id="resource-type"
                >
                  <NativeSelect.Option
                    label="- Select -"
                    value=""
                    key="resourceTypeSelect"
                  />
                  {Object.entries(resourceTypes).map(
                    ([resourceKey, resourceValue]) => (
                      <NativeSelect.Option
                        label={resourceKey}
                        value={resourceValue}
                        key={resourceValue}
                      />
                    ),
                  )}
                </NativeSelect>
              </FormItem>

              <FormItem
                label="Resource URL*"
                message={errors.resourceURL?.message}
                status={errors.resourceURL ? 'error' : 'default'}
                size="md"
              >
                <CKFileUpload
                  connectorDetails={getCkFinderConfig({
                    type: getCkFileType([CkFinderResourceTypeEnum.RESOURCES]),
                    tenantId: elearning?.currentData?.tenantId ?? '',
                    entityId: elearningId,
                    folderName: 'elearnings',
                  })}
                  defaultFileName={getValues('resourceURL')}
                  fileType="resources"
                  handlerFunction={{ onFileChoose: handleImgUpload }}
                  register={register}
                  registerName="resourceURL"
                />
              </FormItem>
            </FormContainer>
            <div className="mt-3" id="add-resource-modal-button-container">
              <Button
                type="primary"
                htmlType="submit"
                ariaLabel="Submit Form"
                isLoading={postResourceStatus.isLoading}
              >
                Save
              </Button>
            </div>
          </Fieldset>
        </form>
      </Modal>
    </div>
  );
};

export default ModuleResources;
